import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';
import Marquee from '../components/Marquee';
import MoreStories from '../components/MoreStories';
import SEO from '../components/SEO';
import Lightbox from '../components/Lightbox';
import { useLightbox } from '../hooks/useLightbox';

const galleryPhotos = [
  {
    src: '/fashion-backstage.jpg',
    caption: 'Backstage Becoming',
    location: 'The Fitting Room, Hour Zero',
  },
  {
    src: '/fashion-gnl-runway.jpg',
    caption: 'The GNL Runway',
    location: 'A Walk Through Milan',
  },
  {
    src: '/fashion-avant-garde.jpg',
    caption: 'The Avant-Garde Moment',
    location: 'Sculptural Couture, Live on Stage',
  },
  {
    src: '/fashion-trench.jpg',
    caption: 'Between the Lights',
    location: 'Talent Lounge, Warm Tungsten',
  },
  {
    src: '/fashion-concert.jpg',
    caption: 'The Live Production',
    location: 'Headliner Set, Pyrotechnic Light',
  },
];

const FashionExplosionStory = () => {
  const [storyOpen, setStoryOpen] = useState(false);
  const storyRef = useRef(null);
  const lightbox = useLightbox();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleViewStory = () => {
    setStoryOpen(true);
    setTimeout(() => {
      storyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  return (
    <div className="bg-[#0a0a0a] min-h-screen text-white">
      <SEO
        title="Fashion Explosion & Production — Runway, Music & Couture | Dion Magee Events"
        description="A multi-discipline production in Arlington, TX where high fashion, live music, and avant-garde design met one stage. Featuring talented artists and designers, produced by Sir Dion Magee."
        path="/portfolio/fashion-explosion-production"
        image="/fashion-backstage.jpg"
        type="article"
        article={{
          headline: 'Fashion Explosion & Production',
          datePublished: '2018-04-21',
          section: 'Fashion & Production',
        }}
      />

      {/* HERO */}
      <section className="relative h-[90vh] w-full overflow-hidden">
        <img
          src="/fashion-backstage.jpg"
          alt="Fashion Explosion & Production — Backstage Couture Moment"
          className="absolute inset-0 w-full h-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/35 to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-transparent to-black/55" />

        <div className="absolute top-8 left-6 lg:top-12 lg:left-12 z-20">
          <Link
            to="/#portfolio"
            data-testid="back-to-portfolio"
            className="inline-flex items-center gap-3 text-white/80 hover:text-[#c9a96e] transition-colors text-[11px] tracking-[0.4em] uppercase"
          >
            <ArrowLeft size={16} />
            Back to Portfolio
          </Link>
        </div>

        <div className="relative z-10 h-full flex flex-col items-center justify-end pb-20 lg:pb-28 px-6 text-center">
          <p className="text-[#c9a96e] text-[11px] tracking-[0.6em] uppercase mb-6">
            A Production Story
          </p>
          <h1
            className="font-serif italic text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl max-w-5xl leading-[1.05]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Fashion Explosion &amp; Production
          </h1>
          <div className="w-16 h-px bg-[#c9a96e] mt-10" />
          <p className="text-white/70 text-xs sm:text-sm tracking-[0.3em] uppercase mt-6 max-w-3xl">
            Runway &nbsp;&middot;&nbsp; Live Music &nbsp;&middot;&nbsp; Avant-Garde Couture
          </p>
          <p className="text-white/55 text-[10px] tracking-[0.4em] uppercase mt-3">
            Arlington, Texas
          </p>
        </div>
      </section>

      {/* TOP GALLERY - 3 chapters */}
      <section className="bg-[#0a0a0a] py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-[1500px] mx-auto">
          <div className="text-center mb-14">
            <p className="text-[#c9a96e] text-[11px] tracking-[0.5em] uppercase mb-5">The Chapters</p>
            <h2 className="font-serif text-white text-4xl md:text-5xl lg:text-6xl">
              One Stage, Many Voices
            </h2>
            <div className="w-12 h-px bg-[#c9a96e] mx-auto mt-7" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
            {galleryPhotos.slice(0, 3).map((p, i) => (
              <figure
                key={i}
                data-testid={`fashion-photo-${i}`}
                onClick={() => lightbox.openAt(i)}
                className="group relative aspect-[4/5] overflow-hidden bg-black cursor-zoom-in"
              >
                <img
                  src={p.src}
                  alt={p.caption}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                <span className="absolute top-4 right-4 w-7 h-7 border-t border-r border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-4 left-4 w-7 h-7 border-b border-l border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <figcaption className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                  <p className="text-[#c9a96e] text-[10px] tracking-[0.4em] uppercase mb-2">Chapter {String(i + 1).padStart(2, '0')}</p>
                  <h3
                    className="font-serif italic text-white text-2xl md:text-3xl leading-tight"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {p.caption}
                  </h3>
                  <p className="text-white/60 text-xs tracking-wider mt-2">{p.location}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* VIEW STORY CTA */}
      <section className="relative bg-[#0a0a0a] py-16 lg:py-24 px-6">
        <div className="pointer-events-none absolute -top-20 left-1/4 w-64 h-64 rounded-full bg-[#c9a96e]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 right-1/4 w-72 h-72 rounded-full bg-[#c9a96e]/10 blur-3xl" />

        <div className="relative max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-6 mb-10">
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-r from-transparent to-[#c9a96e]/60" />
            <span className="block w-2 h-2 rotate-45 border border-[#c9a96e]/80" />
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-l from-transparent to-[#c9a96e]/60" />
          </div>

          <p className="text-[#c9a96e] text-[11px] tracking-[0.6em] uppercase mb-6">Couture Meets Concert</p>
          <h3
            className="font-serif italic text-white text-3xl md:text-4xl lg:text-5xl mb-10 leading-snug"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Step behind the curtain &mdash; where designers, artists, and producers built one unforgettable night.
          </h3>

          <button
            type="button"
            onClick={handleViewStory}
            data-testid="view-story-button"
            className="group relative inline-block px-14 sm:px-20 py-6 sm:py-7 border border-[#c9a96e] bg-transparent overflow-hidden transition-colors duration-500 hover:bg-[#c9a96e]/10"
          >
            <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#e8d5a8]" />
            <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-[#e8d5a8]" />
            <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[#e8d5a8]" />
            <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#e8d5a8]" />
            <span
              className="font-serif italic text-[#e8d5a8] text-2xl sm:text-3xl tracking-[0.2em] relative z-10"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              View Story
            </span>
          </button>

          <div className="flex items-center justify-center gap-6 mt-10">
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-r from-transparent to-[#c9a96e]/60" />
            <span className="block w-2 h-2 rotate-45 border border-[#c9a96e]/80" />
            <span className="block w-24 sm:w-32 h-px bg-gradient-to-l from-transparent to-[#c9a96e]/60" />
          </div>
        </div>
      </section>

      {/* STORY NARRATIVE */}
      <section
        ref={storyRef}
        className={`bg-[#0a0a0a] px-6 lg:px-12 overflow-hidden transition-all duration-700 ease-out ${
          storyOpen ? 'max-h-[6000px] opacity-100 py-20 lg:py-28' : 'max-h-0 opacity-0 py-0'
        }`}
      >
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-[#c9a96e] text-[11px] tracking-[0.55em] uppercase mb-6">The Story</p>
            <h2
              className="font-serif italic text-white text-4xl md:text-5xl lg:text-6xl"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Where Couture Meets Concert
            </h2>
            <div className="w-12 h-px bg-[#c9a96e] mx-auto mt-8" />
          </div>

          <div
            className="space-y-7 text-white/80 text-lg leading-[1.9] font-light"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="fashion-story-text"
          >
            <p className="first-letter:font-serif first-letter:italic first-letter:text-[#c9a96e] first-letter:text-7xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85]">
              Some productions are events. Others are explosions &mdash; collisions
              of disciplines that leave a room changed. In Arlington, Texas,
              we set out to build one of the latter: a single night where high
              fashion, live music, and avant-garde design would share the same
              breath, the same stage, the same audience.
            </p>

            <p>
              For nearly three decades, Dion Magee Events has produced
              high-profile shows featuring some of the most talented artists
              and designers working today. Each production demands more than
              logistics; it demands curation &mdash; the careful pairing of voices
              that, together, become something larger than themselves. This
              show was no different. We assembled designers from three cities,
              recording artists fresh from tour, dancers, lighting directors,
              and a crew of stylists who could turn a fitting room into a
              chapel.
            </p>

            <p>
              The night began before any guest knew it had. In the backstage
              lounge, our team wrapped the opening model in cascading clouds of
              tulle &mdash; the cool light of monitor screens flickering across her
              face as the director called the run order. Two hours, fifteen
              looks, and an entire band to introduce. Hour zero was a quiet
              storm.
            </p>

            <p>
              Then the doors opened. The first runway sequence was a tribute
              to Milan &mdash; deep grey furs, leather sashes, and silhouettes that
              moved like skylines. As the GNL collection finished its closing
              walk, the lights softened, the screens shifted, and our headlining
              band rose from the wings on hydraulic risers. Cyan light split the
              stage. Pink wash painted the front row. A single guitar chord
              earned a standing ovation.
            </p>

            <p>
              In the talent lounge between sets, designers and recording
              artists rehearsed transitions in warm tungsten light. A young
              menswear talent in a deconstructed trench coat ran his lines
              once more, then stepped out into the spotlight to introduce the
              next collection. The choreography held. The crowd kept rising.
            </p>

            <p>
              The closing walk was the riskiest moment of the night &mdash; a
              sculptural white couture piece worn with a fractured ceramic
              mask. It was the kind of moment that doesn&rsquo;t apologize.
              When the model paused at the end of the runway, the room went
              completely silent. And then it broke into applause that didn&rsquo;t
              stop until the band returned for an encore.
            </p>

            <p className="text-center text-[#c9a96e] italic pt-6">
              &mdash; Some events tell a story. The best ones build a whole new language.
            </p>
          </div>

          {/* BOTTOM GALLERY - 2 chapters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 mt-20 max-w-4xl mx-auto">
            {galleryPhotos.slice(3).map((p, i) => (
              <figure
                key={i + 3}
                data-testid={`fashion-photo-${i + 3}`}
                onClick={() => lightbox.openAt(i + 3)}
                className="group relative aspect-[4/5] overflow-hidden bg-black cursor-zoom-in"
              >
                <img
                  src={p.src}
                  alt={p.caption}
                  className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                <span className="absolute top-4 right-4 w-7 h-7 border-t border-r border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-4 left-4 w-7 h-7 border-b border-l border-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <figcaption className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                  <p className="text-[#c9a96e] text-[10px] tracking-[0.4em] uppercase mb-2">Chapter {String(i + 4).padStart(2, '0')}</p>
                  <h3
                    className="font-serif italic text-white text-2xl md:text-3xl leading-tight"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {p.caption}
                  </h3>
                  <p className="text-white/60 text-xs tracking-wider mt-2">{p.location}</p>
                </figcaption>
              </figure>
            ))}
          </div>

          <div className="text-center mt-20">
            <div className="w-16 h-px bg-[#c9a96e] mx-auto mb-8" />
            <p className="text-[#c9a96e] text-[11px] tracking-[0.5em] uppercase mb-6">An Original Production by</p>
            <p
              className="font-serif italic text-white text-3xl"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Dion Magee Events &amp; Weddings
            </p>
            <Link
              to="/#contact"
              data-testid="story-cta-enquire"
              className="inline-block mt-10 px-10 py-4 border border-[#c9a96e] text-[#e8d5a8] text-[11px] tracking-[0.4em] uppercase hover:bg-[#c9a96e] hover:text-black transition-colors duration-500"
            >
              Produce Your Show With Us
            </Link>
          </div>
        </div>
      </section>

      <MoreStories currentSlug="fashion-explosion-production" />
      <Marquee />
      <Footer />
      <Lightbox
        open={lightbox.open}
        onClose={lightbox.close}
        photos={galleryPhotos}
        index={lightbox.index}
        onIndexChange={lightbox.setIndex}
      />
    </div>
  );
};

export default FashionExplosionStory;
